import type { AxiosInstance } from 'axios'
import type { OpenApiSpec } from './openapiSchema.types'

/**
 * Published OpenAPI document of the external API (`/api/v1`), served by the
 * backend per organisation behind the same JWT + `developer_platform:read`
 * permission as the rest of the Developer Platform:
 *
 *   GET integrations/v1/orgs/{org_id}/api-docs/openapi.json
 *
 * Unlike the backend's root `/openapi.json`, this document only contains the
 * external routes, carries the `x-api-key` security scheme, per-endpoint
 * scopes, the error envelope and the overview text — so it is the source the
 * API Reference pages render from.
 */
export function apiDocsPath(orgId: string): string {
  return `integrations/v1/orgs/${encodeURIComponent(orgId)}/api-docs/openapi.json`
}

export function isOpenApiSpec(value: unknown): value is OpenApiSpec {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    'paths' in value &&
    typeof (value as { paths: unknown }).paths === 'object' &&
    (value as { paths: unknown }).paths !== null &&
    !Array.isArray((value as { paths: unknown }).paths)
  )
}

/** Fetches the organisation's external API document through the authenticated client. */
export async function fetchExternalOpenApiSpec(
  apiClient: AxiosInstance,
  orgId: string
): Promise<OpenApiSpec> {
  const { data } = await apiClient.get<unknown>(apiDocsPath(orgId))
  if (!isOpenApiSpec(data)) {
    throw new Error('The api-docs response is not an OpenAPI document (no `paths`).')
  }
  return data
}
